import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { RELYING_PARTY_DETAILS_REFERENCE_DATA_TYPES, RelyingPartyDetailsReferenceData } from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getRelyingPartyDetailsReduxRequest } from "@redux-requests/relying-party-details";
import { ContentPageHeader } from "../common/content-page-header/content-page-header";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { CardContainer } from "../common/card-container/card-container";
import { ContentPageBody } from "../common/content-page-body/content-page-body";
import { RelyingPartyDetailsComponent } from "./relying-party-details-component/relying-party-details-component";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { getRelyingPartyDetailsDataState } from "@redux/selectors/relying-party-details-selectors";


export const RelyingPartyDetailsContainer = () => {
    const { relyingPartyId } = useParams<{ relyingPartyId: string }>();

    const dispatch = useDispatch();

    const {
        isLoadingRelyingParty,
        relyingParty
    } = useSelector(getRelyingPartyDetailsDataState);
    const referenceData = useReferenceDataSelector<RelyingPartyDetailsReferenceData>(RELYING_PARTY_DETAILS_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadRelyingParty, setShouldLoadRelyingParty] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.relyingPartyDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                RELYING_PARTY_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.RELYING_PARTY_DETAILS
            );
        }
    }, [referenceData.isLoading.relyingPartyDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnCompanies,
        referenceData.documentationLinks
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingRelyingParty && shouldLoadRelyingParty) {
            setShouldLoadRelyingParty(false);
            getRelyingPartyDetailsReduxRequest(dispatch)(relyingPartyId, referenceData.axnCompanies);
        }
    }, [isReferenceDataPresent, isLoadingRelyingParty, shouldLoadRelyingParty]);

    const isLoading = useMemo(() => {
        return isLoadingRelyingParty || referenceData.isLoading.relyingPartyDetails;
    }, [referenceData.isLoading.relyingPartyDetails, isLoadingRelyingParty]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            relyingParty &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadRelyingParty
        );
    }, [isLoading, relyingParty, isReferenceDataPresent, shouldLoadReferenceData, shouldLoadRelyingParty]);

    return (
        <CardContainer>
            <ContentPageHeader
                title={resources.pages.relyingPartyDetails.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"relying-party-details-page-header"}
                    />
                }
            />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <RelyingPartyDetailsComponent
                        relyingParty={relyingParty}
                        axnCompanies={referenceData.axnCompanies}
                    />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};